import { useCallback, useContext, useEffect, useState } from "react";

// style
import "../../AdminDashboard.css";
import Style from "./ProjectInfo.module.css";

// context
import { AdminDashboardContext } from "../../../../../context/AdminDashboardContext.js";
import { ProjectInfoContext } from "../../../../../context/ProjectInfoContext.js";

// constants
import {
  firstFormQuestions,
  secondFormQuestions,
} from "../../../../../constants/formQuestions.js";
import { formatDateToUTC2 } from "../../../../../utils/date.js";

// my components
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CalendarInputAdvanced from "../../../../InputComponents/CalendarInputAdvanced/CalendarInputAdvanced.js";
import DropdownDepartmentsInput from "../../../../InputComponents/DropdownDepartmentsInput/DropdownDepartmentsInput.js";
import SpecialInputProjectPartnerExpensesContainer from "../../../../InputComponents/SpecialInputForm/SpecialInputProjectPartnersExpensesContainer/SpecialInputProjectPartnersExpensesContainer.jsx";
import PdfDownload from "../../../../downloadComponents/PdfDownload.jsx";
import CsvConverter from "../../../../downloadComponents/csv/CsvConverter.jsx";
import AdminDashboardProjectSummary from "../../AdminDashboardProjectComponents/AdminDashboardProjectSummary/AdminDashboardProjectSummary.js";
import AdminDashboardProjectTeam from "../../AdminDashboardProjectComponents/AdminDashboardProjectTeam/AdminDashboardProjectTeam.js";
import AdminQuestion from "../../AdminDashboardProjectComponents/AdminQuestion/AdminQuestion.js";
import AdminRadioInput from "../../AdminDashboardProjectComponents/AdminRadioInput/AdminRadioInput.js";
import AdminTextInput from "../../AdminDashboardProjectComponents/AdminTextInput/AdminTextInput.js";
import AdminDashboardNewEmploymentPositions from "../AdminDashboardNewEmploymentPositions/AdminDashboardNewEmploymentPositions.js";
import ProjectPartnersExpensesList from "../ProjectPartnersExpensesList/ProjectPartnersExpensesList.jsx";

const ProjectInfo = ({ selectedProject, departments }) => {
  const [selectedProjectData, setSelectedProjectData] =
    useState(selectedProject);
  const [projectPartnersExpenses, setProjectPartnersExpenses] = useState(
    mapProjectExpenses(selectedProject?.projectPartnersExpenses)
  );

  function mapProjectExpenses(projectPartnersExpenses) {
    return projectPartnersExpenses?.map((partner) => {
      return {
        projectinfosId: selectedProject.id,
        name: partner.name,
        budget: partner.budget,
      };
    });
  }

  const [equipmentsExpenses, setEquipmentsExpenses] = useState(
    mapProjectExpenses(selectedProject.equipmentsExpenses)
  );

  const [departmentId, setDepartmentId] = useState(
    selectedProject?.departmentId || ""
  );
  const [_employmentBoolean, setNewEmploymentBoolean] = useState(
    selectedProject.newEmploymentBoolean ?? false
  );

  const [_consultantServices, setConsultantServices] = useState(
    selectedProject.consultantServices
  );

  const findCurrentDepartment = useCallback(
    (departmentId) => {
      const option = departments?.find((option) => option.id === departmentId);
      return option?.fullName;
    },
    [departments]
  );

  const [departmentLabel, setDepartmentLabel] = useState(
    findCurrentDepartment(selectedProject.departmentId)
  );
  const {
    intentionSelection,
    approvalSelection,
    setUpdateProjectData,
    projectEditable,
    draftSelection,
  } = useContext(AdminDashboardContext);

  useEffect(() => {
    setProjectPartnersExpenses(
      mapProjectExpenses(selectedProjectData.projectPartnersExpenses)
    );
    setEquipmentsExpenses(
      mapProjectExpenses(selectedProjectData.equipmentsExpenses)
    );
  }, [selectedProjectData]);

  useEffect(() => {
    setSelectedProjectData(selectedProject);
    setDepartmentLabel(findCurrentDepartment(selectedProject.departmentId));
  }, [selectedProject, findCurrentDepartment]);

  useEffect(() => {
    setDepartmentLabel(findCurrentDepartment(departmentId));
    setUpdateProjectData((prev) => ({
      ...prev,
      departmentId,
    }));
  }, [departmentId, setUpdateProjectData, findCurrentDepartment]);

  useEffect(() => {
    setUpdateProjectData((prev) => ({
      ...prev,
      projectPartnersExpenses,
    }));
  }, [projectPartnersExpenses]);

  useEffect(() => {
    setUpdateProjectData((prev) => ({
      ...prev,
      equipmentsExpenses: equipmentsExpenses,
    }));
  }, [equipmentsExpenses]);

  return (
    <ProjectInfoContext.Provider value={{ projectinfosId: selectedProject.id }}>
      <div className={Style.ProjectInfoContainer}>
        <h4>Identifikator dokumenta: {selectedProject.id}</h4>
        <h2>
          {selectedProject.nameSurname} - {selectedProject.projectAcronym}
        </h2>

        {!draftSelection && (
          <div className={Style.ConvertToCsvContainer}>
            <p
              style={{ color: "#515151", fontWeight: "bold", fontSize: "1em" }}
            >
              Dohvati projekt u obliku csv datoteke:{" "}
            </p>
            <CsvConverter jsonData={selectedProject} />
          </div>
        )}
        <h4>
          Datum podnošenja namjere: <br />
          {formatDateToUTC2(selectedProject.createdAt.split("T")[0])}
        </h4>
        {approvalSelection && (
          <h4>
            Datum podnošenja ili zadnje izmjene suglasnosti: <br />
            {formatDateToUTC2(selectedProject.updatedAt.split("T")[0])}
          </h4>
        )}
        {intentionSelection && (
          <>
            <AdminQuestion questionText={firstFormQuestions[0]} />
            <AdminTextInput
              currentLabelValue={"IME I PREZIME"}
              currentInputValue={selectedProject.nameSurname}
              projectUpdateName={"nameSurname"}
            />
            {projectEditable ? (
              <DropdownDepartmentsInput
                name={"department"}
                label={"ZAVOD (ODJSEK)"}
                data={departments}
                initialValue={departmentLabel}
                setSpecificState={setDepartmentId}
              />
            ) : (
              <AdminTextInput
                currentLabelValue={"ZAVOD (ODJSEK)"}
                currentInputValue={departmentLabel}
                projectUpdateName={"department"}
              />
            )}

            <AdminTextInput
              currentLabelValue={"TITULA"}
              currentInputValue={selectedProject.vocation}
              projectUpdateName={"vocation"}
            />
            <AdminTextInput
              currentLabelValue={"E-MAIL"}
              currentInputValue={selectedProject.email}
              projectUpdateName={"email"}
            />

            <AdminQuestion questionText={firstFormQuestions[1]} />
            <AdminTextInput
              currentLabelValue={"NAZIV PROJEKTA"}
              currentInputValue={selectedProject.projectTitle}
              projectUpdateName={"projectTitle"}
            />
            <AdminTextInput
              currentLabelValue={"NAZIV PROJEKTA NA ENGLESKOM JEZIKU"}
              currentInputValue={selectedProject.projectTitleEnglish}
              projectUpdateName={"projectTitleEnglish"}
            />
            <AdminTextInput
              currentLabelValue={"AKRONIM PROJEKTA"}
              currentInputValue={selectedProject.projectAcronym}
              projectUpdateName={"projectAcronym"}
            />
            <div style={{ alignSelf: "start" }}>
              <CalendarInputAdvanced
                label={"ROK ZA PRIJAVU PROJEKTA"}
                initialDate={selectedProject?.applicationDeadline || null}
                setSpecificState={setSelectedProjectData}
                placeholderText={"Odaberite datum"}
                isAdminDashboard={true}
                projectUpdateName={"applicationDeadline"}
                workingDaysLimit={0}
              />
            </div>

            <AdminQuestion questionText={firstFormQuestions[2]} />
            <AdminDashboardProjectSummary
              currentLabelValue={"SAŽETAK PROJEKTA"}
              currentInputValue={selectedProject.projectSummary}
              projectUpdateName={"projectSummary"}
            />

            <AdminQuestion questionText={firstFormQuestions[3]} />
            <AdminTextInput
              currentLabelValue={"LINK NA STRANICU"}
              currentInputValue={selectedProject.applicationURL}
              projectUpdateName={"applicationURL"}
            />

            <AdminQuestion questionText={firstFormQuestions[4]} />
            <AdminTextInput
              currentLabelValue={
                "PRIJAVITELJ PROJEKTA/VODEĆI PARTNER (institucija, tvrtka..."
              }
              currentInputValue={selectedProject.projectApplicant}
              projectUpdateName={"projectApplicant"}
            />

            <AdminQuestion questionText={firstFormQuestions[5]} />
            <AdminTextInput
              currentLabelValue={""}
              currentInputValue={selectedProject.projectPartners}
              projectUpdateName={"projectPartners"}
            />

            <AdminQuestion questionText={firstFormQuestions[6]} />
            <AdminTextInput
              currentLabelValue={"UKUPNA VRIJEDNOST (EUR)"}
              currentInputValue={selectedProject.totalValue}
              projectUpdateName={""}
              isNumberInput={true}
            />
            <AdminTextInput
              currentLabelValue={"DIO PRORAČUNA FESB (EUR)"}
              currentInputValue={selectedProject.fesbValuePart}
              projectUpdateName={"fesbValuePart"}
              isNumberInput={true}
            />

            <AdminQuestion questionText={firstFormQuestions[7]} />

            {!projectEditable ? (
              <AdminTextInput
                currentLabelValue={"NOVOZAPOSLENJE"}
                currentInputValue={
                  selectedProject.newEmploymentBoolean ? "Da" : "Ne"
                }
                projectUpdateName={"newEmploymentBoolean"}
              />
            ) : (
              <AdminRadioInput
                name="newEmploymentBoolean"
                simpleQuestionValue={""}
                initialValue={selectedProject.newEmploymentBoolean}
                fieldToUpdate={"newEmploymentBoolean"}
                setSpecificState={setNewEmploymentBoolean}
              />
            )}

            {selectedProject.newEmploymentBoolean && (
              <AdminDashboardNewEmploymentPositions />
            )}

            <AdminQuestion questionText={firstFormQuestions[8]} />
            <AdminDashboardProjectTeam />
          </>
        )}

        {(approvalSelection || draftSelection) && (
          <>
            <div className={Style.pdfContainer}>
              <div className={Style.pdfTitle}>Projektna dokumentacija:</div>
              <div className={Style.pdfContent}>
                {Array.isArray(selectedProject?.pdfDocuments) &&
                  selectedProject.pdfDocuments.slice(0, 10).map((pdf) => (
                    <div className={Style.pdfDivInside} key={pdf.id}>
                      <FontAwesomeIcon
                        icon={faFilePdf}
                        size="xl"
                        style={{ color: "var(--label-color)" }}
                      />
                      <PdfDownload
                        filename={pdf.filename}
                        filepath={pdf.filepath}
                        className="pdfDownloadLink"
                      />
                    </div>
                  ))}
              </div>
            </div>
            <AdminQuestion questionText={secondFormQuestions[0]} />
            <AdminTextInput
              currentLabelValue={"IME I PREZIME"}
              currentInputValue={selectedProject.nameSurname}
              projectUpdateName={"nameSurname"}
            />
            {projectEditable ? (
              <div style={{ alignSelf: "start" }}>
                <DropdownDepartmentsInput
                  name={"department"}
                  label={"ZAVOD (ODJSEK)"}
                  data={departments}
                  initialValue={departmentLabel ? departmentLabel : ""}
                  setSpecificState={setDepartmentId}
                />
              </div>
            ) : (
              <AdminTextInput
                currentLabelValue={"ZAVOD (ODJSEK)"}
                currentInputValue={departmentLabel}
                projectUpdateName={"department"}
              />
            )}
            <AdminTextInput
              currentLabelValue={"TITULA"}
              currentInputValue={selectedProject.vocation}
              projectUpdateName={"vocation"}
            />
            <AdminTextInput
              currentLabelValue={"E-MAIL"}
              currentInputValue={selectedProject.email}
              projectUpdateName={"email"}
            />
            <AdminTextInput
              currentLabelValue={"MOBITEL*"}
              currentInputValue={selectedProject.mobilePhoneNumber}
              projectUpdateName={"mobilePhoneNumber"}
              isPhoneNumberInput={true}
            />
            <AdminTextInput
              currentLabelValue={
                "POSTOTAK RADNOG VREMENA U OKVIRU PREDLOŽENOG PROJEKTA*"
              }
              currentInputValue={selectedProject.workTimeThisPercentage}
              projectUpdateName={"workTimeThisPercentage"}
              isNumberInput={true}
            />
            <AdminTextInput
              currentLabelValue={
                "OSTALI PROJEKTI NA KOJIMA RADI PRIJAVITELJ PROJEKTA I U KOJEM POSTOTKU"
              }
              currentInputValue={selectedProject.workTimeOtherPercentage}
              projectUpdateName={"workTimeOtherPercentage"}
            />
            <AdminTextInput
              currentLabelValue={"NAPOMENA"}
              currentInputValue={selectedProject.teamLeaderNote}
              projectUpdateName={"teamLeaderNote"}
            />
            <AdminQuestion questionText={secondFormQuestions[1]} />
            <AdminTextInput
              currentLabelValue={"NAZIV PROJEKTA"}
              currentInputValue={selectedProject.projectTitle}
              projectUpdateName={"projectTitle"}
            />
            <AdminTextInput
              currentLabelValue={"NAZIV PROJEKTA NA ENGLESKOM JEZIKU"}
              currentInputValue={selectedProject.projectTitleEnglish}
              projectUpdateName={"projectTitleEnglish"}
            />
            <AdminTextInput
              currentLabelValue={"AKRONIM PROJEKTA"}
              currentInputValue={selectedProject.projectAcronym}
              projectUpdateName={"projectAcronym"}
            />
            <div style={{ alignSelf: "start" }}>
              <CalendarInputAdvanced
                label={"ROK ZA PRIJAVU PROJEKTA"}
                name={"applicationDeadline"}
                initialDate={selectedProject?.applicationDeadline || null}
                setSpecificState={setSelectedProjectData}
                placeholderText={"Odaberite datum"}
                isAdminDashboard={true}
                projectUpdateName={"applicationDeadline"}
                workingDaysLimit={0}
              />
            </div>
            <AdminQuestion questionText={secondFormQuestions[2]} />
            <AdminDashboardProjectSummary
              currentLabelValue={"SAŽETAK PROJEKTA"}
              currentInputValue={selectedProject.projectSummary}
              projectUpdateName={"projectSummary"}
            />
            <AdminQuestion questionText={secondFormQuestions[3]} />
            <AdminTextInput
              currentLabelValue={"LINK NA STRANICU"}
              currentInputValue={selectedProject.applicationURL}
              projectUpdateName={"applicationURL"}
            />
            <AdminQuestion questionText={secondFormQuestions[4]} />
            <AdminTextInput
              currentLabelValue={""}
              currentInputValue={selectedProject.sourceOfFunding}
              projectUpdateName={"sourceOfFunding"}
            />
            <AdminQuestion questionText={secondFormQuestions[5]} />
            <AdminTextInput
              currentLabelValue={""}
              currentInputValue={selectedProject.projectType}
              projectUpdateName={"projectType"}
            />
            <AdminQuestion questionText={secondFormQuestions[6]} />
            <div style={{ alignSelf: "start" }}>
              <CalendarInputAdvanced
                label={""}
                name={"expectedProjectBeginning"}
                initialDate={selectedProject?.expectedProjectBeginning || null}
                setSpecificState={setSelectedProjectData}
                placeholderText={"Odaberite datum"}
                isAdminDashboard={true}
                projectUpdateName={"expectedProjectBeginning"}
                workingDaysLimit={0}
              />
            </div>
            <AdminQuestion questionText={secondFormQuestions[7]} />
            <AdminTextInput
              currentLabelValue={"TRAJANJE PROJEKTA (U MJESECIMA)"}
              currentInputValue={
                selectedProject.expectedProjectDurationInMonths
              }
              projectUpdateName={"expectedProjectDurationInMonths"}
              isNumberInput={true}
            />
            <AdminQuestion questionText={secondFormQuestions[8]} />
            <AdminTextInput
              currentLabelValue={"PRIJAVITELJ PROJEKTA/VODEĆI PARTNER"}
              currentInputValue={selectedProject.projectApplicant}
              projectUpdateName={"projectApplicant"}
            />
            <AdminQuestion questionText={secondFormQuestions[9]} />
            <AdminTextInput
              currentLabelValue={"PARTNERI NA PROJEKTU"}
              currentInputValue={selectedProject.projectPartners}
              projectUpdateName={"projectPartners"}
            />
            <AdminQuestion questionText={secondFormQuestions[10]} />
            {projectEditable ? (
              <AdminRadioInput
                name="economicSubjectInvolvement"
                simpleQuestionValue={""}
                initialValue={selectedProject.economicSubjectInvolvement}
                fieldToUpdate={"economicSubjectInvolvement"}
              />
            ) : (
              <AdminTextInput
                currentLabelValue={"UKLJUČENOST GOSPODARSKOG SUBJEKTA"}
                currentInputValue={
                  selectedProject.economicSubjectInvolvement ? "Da" : "Ne"
                }
                projectUpdateName={"economicSubjectInvolvement"}
              />
            )}
            <AdminQuestion questionText={secondFormQuestions[11]} />
            <AdminTextInput
              currentLabelValue={"UKUPNA VRIJEDNOST (EUR)"}
              currentInputValue={selectedProject.totalValue}
              projectUpdateName={"totalValue"}
              isNumberInput={true}
            />
            <AdminQuestion questionText={secondFormQuestions[12]} />
            <AdminTextInput
              currentLabelValue={"DIO PRORAČUNA KOJI PRIPADA FESB-u (EUR)"}
              currentInputValue={selectedProject.fesbValuePart}
              projectUpdateName={"fesbValuePart"}
              isNumberInput={true}
            />
            <AdminTextInput
              currentLabelValue={"TROŠAK POSTOJEĆEG OSOBLJA (EUR)"}
              currentInputValue={selectedProject.currentPersonnelExpense}
              projectUpdateName={"currentPersonnelExpense"}
              isNumberInput={true}
            />
            <AdminTextInput
              currentLabelValue={"TROŠAK NOVOZAPOSLENOG OSOBLJA (EUR)"}
              currentInputValue={selectedProject.newPersonnelExpense}
              projectUpdateName={"newPersonnelExpense"}
              isNumberInput={true}
            />
            <AdminTextInput
              currentLabelValue={"NEIZRAVNI TROŠKOVI (EUR)"}
              currentInputValue={selectedProject.indirectExpenses}
              projectUpdateName={"indirectExpenses"}
              isNumberInput={true}
            />
            {selectedProject.indirectExpensesManualInput && (
              <p>Neizravni troškovi su uneseni ručno</p>
            )}
            <AdminTextInput
              currentLabelValue={"TROŠAK OPREME (EUR)"}
              currentInputValue={selectedProject.equipmentDescriptionAndExpense}
              projectUpdateName={"equipmentDescriptionAndExpense"}
              isNumberInput={true}
            />

            <AdminTextInput
              currentLabelValue={"TROŠAK AMORTIZACIJE OPREME (EUR)"}
              currentInputValue={selectedProject.equipmentAmortizationExpense}
              projectUpdateName={"equipmentAmortizationExpense"}
              isNumberInput={true}
            />
            {projectEditable ? (
              <SpecialInputProjectPartnerExpensesContainer
                partnersList={equipmentsExpenses}
                setPartnersList={setEquipmentsExpenses}
                partnerExpense={Math.max(
                  selectedProject.equipmentDescriptionAndExpense,
                  selectedProject.equipmentAmortizationExpense
                )}
              />
            ) : (
              <ProjectPartnersExpensesList partnersList={equipmentsExpenses} />
            )}
            <AdminTextInput
              currentLabelValue={"PROSTORIJA GDJE ĆE SE OPREMA SMJESTITI"}
              currentInputValue={selectedProject.equipmentStorageLocation}
              projectUpdateName={"equipmentStorageLocation"}
            />
            <AdminTextInput
              currentLabelValue={"TROŠAK VANJSKIH USLUGA (EUR)"}
              currentInputValue={selectedProject.otherServicesExpense}
              projectUpdateName={"otherServicesExpense"}
              isNumberInput={true}
            />
            <AdminTextInput
              currentLabelValue={"TROŠAK MATERIJALA I SITNOG INVENTARA (EUR)"}
              currentInputValue={selectedProject.materialExpense}
              projectUpdateName={"materialExpense"}
              isNumberInput={true}
            />
            <AdminTextInput
              currentLabelValue={
                "PUTNI TROŠAK/TROŠAK KOTIZACIJA/STRUČNOG USAVRŠAVANJA (EUR)"
              }
              currentInputValue={
                selectedProject.travelRegistrationEducationExpense
              }
              projectUpdateName={"travelRegistrationEducationExpense"}
              isNumberInput={true}
            />
            <AdminTextInput
              currentLabelValue={"NAPOMENA O TROŠKOVIMA"}
              currentInputValue={selectedProject.expenseNote}
              projectUpdateName={"expenseNote"}
            />
            <AdminQuestion questionText={secondFormQuestions[13]} />
            <AdminTextInput
              currentLabelValue={"TROŠKOVI PARTNERA (EUR)"}
              currentInputValue={selectedProject.partnerExpense}
              projectUpdateName={"partnerExpense"}
              isNumberInput={true}
            />
            {projectEditable ? (
              <SpecialInputProjectPartnerExpensesContainer
                partnersList={projectPartnersExpenses}
                setPartnersList={setProjectPartnersExpenses}
                partnerExpense={selectedProject.partnerExpense}
              />
            ) : (
              <ProjectPartnersExpensesList
                partnersList={projectPartnersExpenses}
              />
            )}
            <AdminQuestion questionText={secondFormQuestions[14]} />
            <AdminTextInput
              currentLabelValue={"AVANSNO PLAĆANJE (EUR)"}
              currentInputValue={selectedProject.downPayment}
              projectUpdateName={"downPayment"}
              isNumberInput={true}
            />
            <AdminQuestion questionText={secondFormQuestions[15]} />
            <AdminTextInput
              currentLabelValue={"PLAN SUFINANCIRANJA"}
              currentInputValue={selectedProject.personalFinancingExpense}
              projectUpdateName={"personalFinancingExpense"}
            />
            <AdminQuestion questionText={secondFormQuestions[16]} />
            {/* Assuming 'newEmploymentBoolean' is a boolean, converting it to a more readable format for display */}

            {projectEditable ? (
              <AdminRadioInput
                name="newEmploymentBoolean"
                simpleQuestionValue={""}
                initialValue={selectedProject.newEmploymentBoolean}
                fieldToUpdate={"newEmploymentBoolean"}
                setSpecificState={setNewEmploymentBoolean}
              />
            ) : (
              <AdminTextInput
                currentLabelValue={"NOVOZAPOSLENJE"}
                currentInputValue={
                  selectedProject.newEmploymentBoolean ? "Da" : "Ne"
                }
                projectUpdateName={"newEmploymentBoolean"}
              />
            )}

            {selectedProject.newEmploymentBoolean && (
              <>
                <AdminTextInput
                  currentLabelValue={"PROSTORIJA ZA SMJEŠTAJ NOVOZAPOSLENIH:"}
                  currentInputValue={selectedProject.newEmploymentsWorkspace}
                  projectUpdateName={"newEmploymentsWorkspace"}
                />
                <AdminDashboardNewEmploymentPositions />
              </>
            )}

            <AdminQuestion questionText={secondFormQuestions[17]} />
            <AdminDashboardProjectTeam />

            <AdminTextInput
              currentLabelValue={"POTREBNA DOKUMENTACIJA"}
              currentInputValue={selectedProject.requiredDocumentationFESB}
              projectUpdateName={"requiredDocumentationFESB"}
            />

            <AdminQuestion questionText={secondFormQuestions[18]} />
            {projectEditable ? (
              <AdminRadioInput
                name="consultantServices"
                simpleQuestionValue={""}
                initialValue={selectedProject.consultantServices}
                fieldToUpdate={"consultantServices"}
                setSpecificState={setConsultantServices}
              />
            ) : (
              <AdminTextInput
                currentLabelValue={""}
                currentInputValue={
                  selectedProject.consultantServices ? "Da" : "Ne"
                }
                projectUpdateName={"consultantServices"}
              />
            )}
            {selectedProject.consultantServices && (
              <>
                <AdminTextInput
                  currentLabelValue={"IZNOS KONZULTANTSKIH USLUGA (EUR)"}
                  currentInputValue={selectedProject.consultantExpense}
                  projectUpdateName={"consultantExpense"}
                  isNumberInput={true}
                />
                <AdminTextInput
                  currentLabelValue={"IZVOR KONZULTANSKIH USLUGA"}
                  currentInputValue={selectedProject.consultantExpenseSource}
                  projectUpdateName={"consultantExpenseSource"}
                />
              </>
            )}
          </>
        )}
      </div>
    </ProjectInfoContext.Provider>
  );
};

export default ProjectInfo;
